import {
  useAnalyticsCartItems,
  useCartAndDeliveryDatesDataContext,
} from '@app/data/clients/cartCheckoutClient';
import { AnalyticsPageTypeProperty } from '@redbubble/next-analytics/gtm';
import type { RSCheckoutLocationKey } from '@redbubble/next-provider/rudderstack';
import { useCallback } from 'react';
import type { PayPalExpressProps } from './PayPalExpress';
import { PayPalExpress } from './PayPalExpress';
import { intlErrorMessages as PayPalMessages } from './paymentErrorLookup';
import type { ErrorMessage, PageType, PayPalErrorCallback } from './types';
import { usePayPalShippingAddressChangeHandler } from './usePayPalShippingAddressChangeHandler';
import { usePayPalShippingPreferenceChangeHandler } from './usePayPalShippingPreferenceChangeHandler';
import {
  mapCartToPayPalRequiredCartFromMonolith,
  onPayPalApproveOrderError,
  onPayPalCreateOrderError,
  onPayPalInitError,
} from './utils';

export interface MonolithCartPaypalExpressContainerProps {
  selectorId: string;
  // page: Identifies the originating page to the monolith backend (via the PayPal checkout API),
  // which uses it to determine the payment_type on a purchase order — values must match what
  // the monolith expects (see types.ts). NOTE: page and analyticsPageType serve different
  // purposes and must remain separate; the two type systems overlap in value but are not
  // interchangeable.
  page: PageType;
  // analyticsPageType: A GTM/GA4 concept used purely for frontend event tracking. Distinct
  // from page — see the note on page above.
  analyticsPageType: AnalyticsPageTypeProperty;
  metricName: string;
  onError: (message: ErrorMessage) => void;
  email?: string | null;
  shippingAddressEditable?: boolean;
  shippingOptionsEditable?: boolean;
  onPayError?: PayPalErrorCallback;
  getAddressOverride?: PayPalExpressProps['getAddressOverride'];
  onStartPayPalExpressCheckout?: () => void;
  onCancel?: () => Promise<void>;
  rsCheckoutLocation: RSCheckoutLocationKey;
}

export const MonolithCartPaypalExpressContainer = ({
  selectorId,
  page,
  analyticsPageType,
  metricName,
  onError,
  email,
  shippingAddressEditable,
  shippingOptionsEditable,
  onPayError,
  getAddressOverride,
  onStartPayPalExpressCheckout,
  onCancel,
  rsCheckoutLocation,
}: MonolithCartPaypalExpressContainerProps) => {
  const { cart } = useCartAndDeliveryDatesDataContext();
  const analyticsItems = useAnalyticsCartItems();

  const { handleShippingAddressChange } =
    usePayPalShippingAddressChangeHandler();
  const { handleShippingPreferenceChange } =
    usePayPalShippingPreferenceChangeHandler();

  const handleInitError: PayPalErrorCallback = useCallback(
    (message) => onPayPalInitError({ message, onError, metricName }),
    [onError, metricName]
  );

  const handleCreateOrderError: PayPalErrorCallback = useCallback(
    (message) => onPayPalCreateOrderError({ message, onError, metricName }),
    [onError, metricName]
  );

  const handleApproveOrderError: PayPalErrorCallback = useCallback(
    (message) => onPayPalApproveOrderError({ message, onError, metricName }),
    [onError, metricName]
  );

  const handlePayError: PayPalErrorCallback = useCallback(
    (message) => {
      if (onPayError) {
        onPayError(message);
      } else {
        onError(message || PayPalMessages.serverError);
      }
    },
    [onError, onPayError]
  );

  if (!cart) return null;

  const payPalRequiredCart = mapCartToPayPalRequiredCartFromMonolith(cart);

  return (
    <PayPalExpress
      cart={payPalRequiredCart}
      selectorId={selectorId}
      page={page}
      analyticsPageType={analyticsPageType}
      email={email ?? null}
      onInitError={handleInitError}
      onCreateOrderError={handleCreateOrderError}
      onApproveOrderError={handleApproveOrderError}
      onPayError={handlePayError}
      shippingAddressEditable={shippingAddressEditable}
      shippingOptionsEditable={shippingOptionsEditable}
      getAddressOverride={getAddressOverride}
      onStartPayPalExpressCheckout={onStartPayPalExpressCheckout}
      onCancel={onCancel}
      onShippingAddressChange={handleShippingAddressChange}
      onShippingPreferenceChange={handleShippingPreferenceChange}
      analyticsItems={analyticsItems}
      rsCheckoutLocation={rsCheckoutLocation}
    />
  );
};
